import type { paths } from "@/api-schema";
import {
  PathsV1BranchUuidGetParametersQueryExtend,
  PathsV1ModelFamilyUuidGetParametersQueryExtend,
  PathsV1RelocationOrderUuidGetParametersQueryExtend,
  PathsV1RuleUuidGetParametersQueryExtend,
  PathsV1VehicleBuyInUuidGetParametersQueryExtend,
  PathsV1VehicleUuidGetParametersQueryExtend,
} from "@/api-schema-enums";
import { useAppRoleByUuidQuery } from "@/common/api/app-role/use-app-role-by-uuid.query";
import { useApplicationByUuidQuery } from "@/common/api/application/use-application-by-uuid.query";
import { useBranchByUuidQuery } from "@/common/api/branch/use-branch-by-uuid.query";
import { useBrandByUuidQuery } from "@/common/api/brand/use-brand-by-uuid.query";
import { useCasaviContactBlacklistByUuidQuery } from "@/common/api/casavi/contact-blacklist/use-casavi-contact-blacklist-by-uuid.query";
import { useCasaviContactWhitelistByUuidQuery } from "@/common/api/casavi/contact-whitelist/use-casavi-contact-whitelist-by-uuid.query";
import { useCompanyByUuidQuery } from "@/common/api/company/use-company-by-uuid.query";
import { useCompanyGroupByUuidQuery } from "@/common/api/company-group/use-company-group-by-uuid.query";
import { useCustomerByUuidQuery } from "@/common/api/customer/use-customer-by-uuid.query";
import { useDepartmentByUuidQuery } from "@/common/api/department/use-department-by-uuid.query";
import { useGroupByUuidQuery } from "@/common/api/group/use-group-by-uuid.query";
import { useJobFunctionByUuidQuery } from "@/common/api/job-function/use-job-function-by-uuid.query";
import { useModelFamilyByUuidQuery } from "@/common/api/model-family/use-model-family-by-uuid.query";
import { useModelGenerationByUuidQuery } from "@/common/api/model-generation/use-model-generation-by-uuid.query";
import { useOccupationByUuidQuery } from "@/common/api/occupation/use-occupation-by-uuid.query";
import { useOpenPositionByUuidQuery } from "@/common/api/open-position/use-open-position-by-uuid.query";
import type { OpenPosition } from "@/common/api/open-position/use-open-position-search.query";
import { useRelocationOrderByUuidQuery } from "@/common/api/relocation-order/use-relocation-order-by-uuid.query";
import { useRuleByUuidQuery } from "@/common/api/rule/use-rule-by-uuid.query";
import { useUserByUuidQuery } from "@/common/api/user/use-user-by-uuid.query";
import { useVehicleBuyInByUuidQuery } from "@/common/api/vehicle-buy-in/use-vehicle-buy-in-by-uuid.query";
import { useVehicleValuationByUuidQuery } from "@/common/api/vehicle-valuation/use-vehicle-valuation-by-uuid.query";
import { useVehicleByUuidQuery } from "@/common/api/vehicle/use-vehicle-by-uuid.query";
import { getCustomerDisplayName } from "@/components/data-table/predefined/customer-table/_helpers/get-customer-display-name";
import { EMPTY_VALUE_PLACEHOLDER } from "@/components/ui/empty-value";
import type { UseQueryResult } from "@tanstack/react-query";
import type { TFunction } from "i18next";

export type BreadcrumbDisableRule = {
  segment: string;
  parentSegment: string;
};

export const breadcrumbDisableWhenParentUuid: BreadcrumbDisableRule[] = [{ segment: "rule", parentSegment: "group" }];

type SuccessResponseRule = paths["/v1/rule/{uuid}"]["get"]["responses"][200]["content"]["application/json"];
type SuccessResponseUser = paths["/v1/user/{uuid}"]["get"]["responses"][200]["content"]["application/json"];
type SuccessResponseBranch = paths["/v1/branch/{uuid}"]["get"]["responses"][200]["content"]["application/json"];
type SuccessResponseRelocationOrder =
  paths["/v1/relocation-order/{uuid}"]["get"]["responses"][200]["content"]["application/json"];
type SuccessResponseVehicle = paths["/v1/vehicle/{uuid}"]["get"]["responses"][200]["content"]["application/json"];
type SuccessResponseCasaviContactWhitelist =
  paths["/v1/casavi-contact-whitelist/{uuid}"]["get"]["responses"][200]["content"]["application/json"];
type SuccessResponseCasaviContactBlacklist =
  paths["/v1/casavi-contact-blacklist/{uuid}"]["get"]["responses"][200]["content"]["application/json"];
type SuccessResponseModelGeneration =
  paths["/v1/model-generation/{uuid}"]["get"]["responses"][200]["content"]["application/json"];
type SuccessResponseModelFamily =
  paths["/v1/model-family/{uuid}"]["get"]["responses"][200]["content"]["application/json"];
type SuccessResponseCustomer = paths["/v1/customer/{uuid}"]["get"]["responses"][200]["content"]["application/json"];
type SuccessResponseVehicleBuyIn =
  paths["/v1/vehicle-buy-in/{uuid}"]["get"]["responses"][200]["content"]["application/json"];
type SuccessResponseVehicleValuation =
  paths["/v1/vehicle-valuation/{uuid}"]["get"]["responses"][200]["content"]["application/json"];

export type BreadcrumbDataMapper = (data: unknown, t: TFunction) => string | null;

export type BreadcrumbUuidResolver = {
  segments: string[];
  useQuery: (uuid: string) => UseQueryResult<unknown>;
  mapData: BreadcrumbDataMapper;
};

export const breadcrumbUuidResolvers: BreadcrumbUuidResolver[] = [
  {
    segments: ["relocation-order", "relocation-request"],
    useQuery: (uuid) =>
      useRelocationOrderByUuidQuery(uuid, {
        parameters: {
          extend: [PathsV1RelocationOrderUuidGetParametersQueryExtend.Vehicle],
        },
      }),
    mapData: (data) => `VIN: ${(data as SuccessResponseRelocationOrder).vehicle.vin}`,
  },
  {
    segments: ["rule"],
    useQuery: (uuid) =>
      useRuleByUuidQuery(uuid, {
        parameters: {
          extend: [
            PathsV1RuleUuidGetParametersQueryExtend.Occupations,
            PathsV1RuleUuidGetParametersQueryExtend.Departments,
            PathsV1RuleUuidGetParametersQueryExtend.JobFunctions,
            PathsV1RuleUuidGetParametersQueryExtend.CompanyGroups,
            PathsV1RuleUuidGetParametersQueryExtend.Companies,
            PathsV1RuleUuidGetParametersQueryExtend.Brands,
            PathsV1RuleUuidGetParametersQueryExtend.Branches,
          ],
        },
      }),
    mapData: (data, t) => mapRuleData(data as SuccessResponseRule, t),
  },
  {
    segments: ["user", "phonebook"],
    useQuery: useUserByUuidQuery,
    mapData: (data) => {
      const typed = data as SuccessResponseUser;
      return `${typed.lastName}, ${typed.firstName}`;
    },
  },
  { segments: ["group"], useQuery: useGroupByUuidQuery, mapData: mapName },
  {
    segments: ["application"],
    useQuery: useApplicationByUuidQuery,
    mapData: mapName,
  },
  { segments: ["app-role"], useQuery: useAppRoleByUuidQuery, mapData: mapName },
  { segments: ["brand"], useQuery: useBrandByUuidQuery, mapData: mapName },
  {
    segments: ["branch"],
    useQuery: (uuid) =>
      useBranchByUuidQuery(uuid, {
        parameters: {
          extend: [
            PathsV1BranchUuidGetParametersQueryExtend.Company,
            PathsV1BranchUuidGetParametersQueryExtend.Brands,
            PathsV1BranchUuidGetParametersQueryExtend.Address,
          ],
        },
      }),
    mapData: (data) => (data as SuccessResponseBranch).name,
  },
  {
    segments: ["vehicle"],
    useQuery: (uuid) =>
      useVehicleByUuidQuery(uuid, {
        parameters: {
          extend: [
            PathsV1VehicleUuidGetParametersQueryExtend.ModelGeneration,
            PathsV1VehicleUuidGetParametersQueryExtend.ModelGeneration_ModelFamily,
            PathsV1VehicleUuidGetParametersQueryExtend.ModelGeneration_ModelFamily_Brand,
          ],
        },
      }),
    mapData: (data) => `VIN: ${(data as SuccessResponseVehicle).vin}`,
  },
  {
    segments: ["initialize"],
    useQuery: (uuid) =>
      useVehicleByUuidQuery(uuid, {
        parameters: {
          extend: [
            PathsV1VehicleUuidGetParametersQueryExtend.ModelGeneration,
            PathsV1VehicleUuidGetParametersQueryExtend.ModelGeneration_ModelFamily,
            PathsV1VehicleUuidGetParametersQueryExtend.ModelGeneration_ModelFamily_Brand,
          ],
        },
      }),
    mapData: (data) => {
      const typed = data as SuccessResponseVehicle;
      return `${typed.modelGeneration?.modelFamily?.brand?.name || ""} ${typed.modelGeneration?.name || ""} - ${typed.vin}`.trim();
    },
  },
  {
    segments: ["contact-whitelist"],
    useQuery: useCasaviContactWhitelistByUuidQuery,
    mapData: (data) => (data as SuccessResponseCasaviContactWhitelist).value,
  },
  {
    segments: ["contact-blacklist"],
    useQuery: useCasaviContactBlacklistByUuidQuery,
    mapData: (data) => (data as SuccessResponseCasaviContactBlacklist).value,
  },
  {
    segments: ["job-function"],
    useQuery: useJobFunctionByUuidQuery,
    mapData: mapName,
  },
  {
    segments: ["department"],
    useQuery: useDepartmentByUuidQuery,
    mapData: mapName,
  },
  {
    segments: ["occupation"],
    useQuery: useOccupationByUuidQuery,
    mapData: mapName,
  },
  { segments: ["company"], useQuery: useCompanyByUuidQuery, mapData: mapName },
  { segments: ["company-group"], useQuery: useCompanyGroupByUuidQuery, mapData: mapName },
  {
    segments: ["model-generation"],
    useQuery: useModelGenerationByUuidQuery,
    mapData: (data) => (data as SuccessResponseModelGeneration).name,
  },
  {
    segments: ["model-family"],
    useQuery: (uuid) =>
      useModelFamilyByUuidQuery(uuid, {
        parameters: {
          extend: [PathsV1ModelFamilyUuidGetParametersQueryExtend.Brand],
        },
      }),
    mapData: (data) => (data as SuccessResponseModelFamily).name,
  },
  {
    segments: ["customer"],
    useQuery: useCustomerByUuidQuery,
    mapData: (data) => getCustomerDisplayName(data as SuccessResponseCustomer),
  },
  {
    segments: ["list"],
    useQuery: useVehicleValuationByUuidQuery,
    mapData: (data) => `VIN: ${(data as SuccessResponseVehicleValuation).vinUsed}`,
  },
  {
    segments: ["buy-in"],
    useQuery: (uuid) =>
      useVehicleBuyInByUuidQuery(uuid, {
        parameters: {
          extend: [PathsV1VehicleBuyInUuidGetParametersQueryExtend.Vehicle],
        },
      }),
    mapData: (data) => `VIN: ${(data as SuccessResponseVehicleBuyIn).vehicle.vin}`,
  },
  {
    segments: ["open-position"],
    useQuery: useOpenPositionByUuidQuery,
    mapData: (data) => (data as OpenPosition).invoiceNumber || EMPTY_VALUE_PLACEHOLDER,
  },
];

function mapRuleData(data: SuccessResponseRule, t: TFunction): string {
  const {
    occupations = [],
    departments = [],
    jobFunctions = [],
    companyGroups = [],
    companies = [],
    brands = [],
    branches = [],
  } = data;

  const parts = [
    occupations.length && t("common:Occupation", { count: occupations.length }),
    departments.length && t("common:Department", { count: departments.length }),
    jobFunctions.length && t("common:JobFunction", { count: jobFunctions.length }),
    companyGroups.length && t("common:CompanyGroup", { count: companyGroups.length }),
    companies.length && t("common:Company", { count: companies.length }),
    brands.length && t("common:Brand", { count: brands.length }),
    branches.length && t("common:Branch", { count: branches.length }),
  ].filter(Boolean);

  if (parts.length) {
    const text = parts.join(", ");
    return text.length > 25 ? `${text.slice(0, 25)}...` : text;
  }

  return t("NoFilters");
}

function mapName(data: unknown): string {
  return (data as { name: string }).name;
}
